import React, { useMemo, useState } from 'react';
import { Check, MonitorPlay, Smartphone, Tv } from 'lucide-react';
import { cn } from '../lib/utils';
import { WEB_PLAYER_OPTION_ID, partnerAppLicenseInfo } from '../lib/partnerApps';
import { API_BASE_URL } from '../apiBase';
import './PartnerAppPicker.css';

export function AppLicenseNotice({ app }) {
  const info = partnerAppLicenseInfo(app);
  return <div className="partner-app-license-notice" data-kind={info.kind} role="status">
    <strong>{info.label}</strong><p>{info.description}</p>
    {info.trial && <p>{info.trial}</p>}{info.note && <p>{info.note}</p>}
  </div>;
}
function LicenseBadge({ app }) {
  const info = partnerAppLicenseInfo(app);
  return <span className="partner-app-license-badge" data-kind={info.kind}>{info.label}</span>;
}

function deviceIcon(devices = []) {
  const blob = (Array.isArray(devices) ? devices.join(' ') : String(devices || '')).toLowerCase();
  if (/samsung|lg|tv|roku|fire|box/.test(blob)) return Tv;
  if (/android|ios|celular|tablet/.test(blob)) return Smartphone;
  return MonitorPlay;
}

function logoSrc(url) {
  const u = String(url || '').trim();
  if (!u) return null;
  if (u.startsWith('http') || u.startsWith('data:')) return u;
  return `${API_BASE_URL}${u.startsWith('/') ? '' : '/'}${u}`;
}

/**
 * Seleção de 1 app parceiro (ou só Web Player).
 */
export default function PartnerAppPicker({
  apps = [],
  webPlayerOption,
  selectedId,
  onChange,
  filterDevice = '',
  className,
  compact = false,
}) {
  const [deviceFilter, setDeviceFilter] = useState(filterDevice || '');
  const list = useMemo(() => {
    const base = [...(apps || [])].filter((a) => a && a.enabled !== false);
    const q = String(deviceFilter || '').trim().toLowerCase();
    if (!q) return base;
    return base.filter((a) => {
      // A brand name must not make an Android APK appear compatible with Tizen/Web.
      if (a.catalog_kind === 'own') return (a.devices || []).some(device => device.toLowerCase() === q);
      const blob = `${a.name || ''} ${(a.devices || []).join(' ')} ${a.devices_label || ''}`.toLowerCase();
      return blob.includes(q);
    });
  }, [apps, deviceFilter]);

  const chips = useMemo(() => {
    const set = new Set();
    (apps || []).forEach((a) => (a.devices || []).forEach((d) => set.add(String(d).trim())));
    return [...set].filter(Boolean).slice(0, 10);
  }, [apps]);

  const webOpt = webPlayerOption || {
    id: WEB_PLAYER_OPTION_ID,
    name: 'Só Web Player / M3U',
    devices_label: 'Web / qualquer dispositivo',
  };

  const selectedApp = selectedId === WEB_PLAYER_OPTION_ID ? webOpt : apps.find(app => String(app.id) === String(selectedId) && app.enabled !== false);

  return (
    <div className={cn('partner-app-picker space-y-3', compact && 'space-y-2', className)}>
      <div className="partner-app-picker__intro"><strong>Escolha um aplicativo · Obrigatório</strong><p>Confira a compatibilidade e as condições da licença antes de continuar.</p></div>
      <AppLicenseNotice app={selectedApp} />

      {chips.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          <button
            type="button"
            onClick={() => setDeviceFilter('')}
            className={cn(
              'min-h-[var(--console-control-small,2rem)] min-w-[var(--console-control-small,2rem)] rounded-md border px-2.5 py-1.5 text-xs font-medium uppercase tracking-wide transition',
              !deviceFilter
                ? 'border-primary bg-primary/15 text-primary'
                : 'border-border/70 text-muted-foreground hover:border-primary/40',
            )}
          >
            Todos
          </button>
          {chips.map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDeviceFilter(d)}
              className={cn(
                'min-h-[var(--console-control-small,2rem)] min-w-[var(--console-control-small,2rem)] rounded-md border px-2.5 py-1.5 text-xs font-medium transition',
                deviceFilter === d
                  ? 'border-primary bg-primary/15 text-primary'
                  : 'border-border/70 text-muted-foreground hover:border-primary/40',
              )}
            >
              {d}
            </button>
          ))}
        </div>
      ) : null}

      {list.filter(app => app.catalog_kind === 'own').map(app => (
        <button key={app.id} type="button" aria-pressed={String(selectedId) === String(app.id)}
          onClick={() => onChange?.(app.id)}
          className={cn('flex w-full items-start gap-3 rounded-[3px] border p-3 text-left transition',
            String(selectedId) === String(app.id) ? 'border-primary bg-primary/10 ring-1 ring-primary/30' : 'border-emerald-500/40 bg-emerald-500/5 hover:border-primary/40')}>
          <span className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-[3px] bg-muted/80">
            {app.logo_url ? <img src={logoSrc(app.logo_url)} alt="" className="h-full w-full object-contain" loading="lazy" decoding="async" /> : <Smartphone className="h-5 w-5 text-primary" />}
          </span>
          <span className="min-w-0 flex-1"><span className="block text-[10px] font-semibold uppercase text-emerald-600 dark:text-emerald-400">Seu aplicativo · Publicado</span>
            <span className="block text-sm font-semibold">{app.name}</span><LicenseBadge app={app} />
            <span className="mt-1 block text-[11px] text-muted-foreground">{app.devices_label || (app.devices || []).join(', ')}</span>
            <span className="mt-1 block text-[11px] text-muted-foreground">APK personalizado · download incluído nas orientações do acesso</span></span>
          {String(selectedId) === String(app.id) && <Check className="h-4 w-4 shrink-0 text-primary" />}
        </button>
      ))}
      <button
        type="button"
        aria-pressed={selectedId === WEB_PLAYER_OPTION_ID}
        onClick={() => onChange?.(WEB_PLAYER_OPTION_ID)}
        className={cn(
          'flex w-full items-start gap-3 rounded-xl border p-3 text-left transition',
          selectedId === WEB_PLAYER_OPTION_ID
            ? 'border-primary bg-primary/10 ring-1 ring-primary/30'
            : 'border-border/80 bg-card/40 hover:border-primary/40',
        )}
      >
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-muted/80">
          <MonitorPlay className="h-5 w-5 text-primary" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold text-foreground">{webOpt.name}</span><LicenseBadge app={webOpt} />
          <span className="mt-0.5 block text-[11px] text-muted-foreground">
            {webOpt.devices_label || 'Sem instalar app — navegador ou lista M3U'}
          </span>
        </span>
        {selectedId === WEB_PLAYER_OPTION_ID ? (
          <Check className="mt-1 h-4 w-4 shrink-0 text-primary" />
        ) : null}
      </button>

      <div
        className={cn(
          'partner-app-picker__grid grid gap-2 overflow-y-auto custom-scrollbar pr-0.5',
          compact ? 'max-h-[min(36vh,260px)]' : 'max-h-[min(42vh,320px)]',
        )}
      >
        {list.filter(app => app.catalog_kind !== 'own').map((app) => {
          const selected = String(selectedId) === String(app.id);
          const Icon = deviceIcon(app.devices);
          const src = logoSrc(app.logo_url);
          return (
            <button
              key={app.id}
              type="button"
              aria-pressed={selected}
              onClick={() => onChange?.(app.id)}
              className={cn(
                'partner-app-picker__option flex items-start gap-3 rounded-xl border p-3 text-left transition',
                selected
                  ? 'border-primary bg-primary/10 ring-1 ring-primary/30'
                  : 'border-border/80 bg-card/40 hover:border-primary/40',
              )}
            >
              <span className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-muted/80">
                {src ? (
                  <img src={src} alt="" className="h-full w-full object-cover" loading="lazy" decoding="async" />
                ) : (
                  <Icon className="h-5 w-5 text-muted-foreground" />
                )}
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex items-start justify-between gap-2">
                  <span className="min-w-0 break-words text-sm font-semibold text-foreground">{app.name}</span>
                  {selected ? <Check className="h-4 w-4 shrink-0 text-primary" /> : null}
                </span>
                <span className="mt-0.5 block text-[11px] leading-snug text-muted-foreground">
                  {app.devices_label || (app.devices || []).join(', ') || 'Dispositivos variados'}
                </span>
                <LicenseBadge app={app} />
                {app.access_code ? (
                  <span className="mt-1 inline-block max-w-full break-all rounded bg-muted/60 px-1.5 py-0.5 font-mono text-[10px] leading-relaxed text-foreground/80">
                    Cód. {app.access_code}
                  </span>
                ) : null}
              </span>
            </button>
          );
        })}
      </div>
      {!list.length ? (
        <p className="text-xs text-muted-foreground">
          Nenhum app neste filtro. Peça ao administrador para cadastrar em Aplicativos.
        </p>
      ) : null}
    </div>
  );
}
